import { inject, Injectable } from '@angular/core';
import { addDoc, collection, doc, FieldValue, Firestore, increment, WriteBatch, writeBatch } from '@angular/fire/firestore';
import { Assessment, Evaluee, Register, RegistrationForm } from '@career-scope/models';
import { PortalService } from './portal.service';
import { map, Observable, take } from 'rxjs';
import { NotificationService } from './notification.service';
import { environment } from '../../environments/environment';
import { Invitation } from '../models/register.model';

@Injectable({
  providedIn: 'root'
})
export class EvalueeRegistrationService {
  firestore = inject(Firestore);
  portalService = inject(PortalService);
  notificationService = inject(NotificationService);

  addEvalueeRegistration(registrationForm: RegistrationForm): Observable<Evaluee | null> {
    return this.portalService.portal.pipe(
      take(1),
      map(portal => {
        if (!portal) {
          this.notificationService.openDismissibleSnackbar('Unable to find portal information');
          throw new Error('Unable to find portal information');
        }

        if (portal.availableAdmins <= 0) {
          this.notificationService.openDismissibleSnackbar('There are no remaining administrations');
          throw new Error('There are no remaining administrations');
        }

        try {
          const batch = writeBatch(this.firestore);
          const evaluee = this.addEvalueeRegistrationToBatch(batch, portal.id, registrationForm);
          batch.update(doc(this.firestore, `portals/${portal.id}`), {
            availableAdmins: increment(-1),
            registrations: increment(1),
            lastModifiedDate: new Date(Date.now())
          } as { availableAdmins: FieldValue; registrations: FieldValue; lastModifiedDate: Date; });
          batch.commit();

          return evaluee;
        } catch (error) {
          console.error('Error adding evaluee registration', error);
          this.notificationService.openDismissibleSnackbar('Error adding evaluee registration');
          throw new Error('Error adding evaluee registration');
        }
      })
    );
  }

  addEvalueeRegistrationToBatch(batch: WriteBatch, portalId: string, registrationForm: RegistrationForm): Evaluee {
    const registrationRef = doc(collection(this.firestore, 'registrations'));
    const registrationId = registrationRef.id;

    const evalueeRef = doc(collection(this.firestore, `portals/${portalId}/evaluees`));
    const evalueeId = evalueeRef.id;

    const assessmentRef = doc(collection(this.firestore, `portals/${portalId}/evaluees/${evalueeId}/assessments`));
    const assessmentId = assessmentRef.id;

    registrationForm.id = registrationId;

    const register: Register = this.createRegistrationObject(registrationForm, portalId, evalueeId);
    const evaluee: Evaluee = this.createEvalueeObject(registrationForm, portalId, evalueeId, assessmentId);
    const assessment: Assessment = createAssessmentObject(evalueeId, portalId, assessmentId, registrationForm.counselor?.id);

    batch.set(registrationRef, <Register>register);
    batch.set(evalueeRef, <Evaluee>evaluee);
    batch.set(assessmentRef, <Assessment>assessment);

    return evaluee;
  }

  createRegistrationObject(register: RegistrationForm, portalId: string, evalueeId: string): Register {
    return {
      id: register.id,
      fullName: register.fullName,
      email: register.email || '',
      assessmentSettings: register.assessmentSettings,
      portalId,
      counselor: register.counselor,
      status: 'new',
      creationDate: new Date(Date.now()),
      creatorId: register.creatorId,
      batchId: register.batchId,
      evalueeId,
      identifiers: register.identifiers || [],
      consumerDirect: register.consumerDirect || false,
    };
  }

  createEvalueeObject(register: RegistrationForm, portalId: string, evalueeId: string, assessmentId: string): Evaluee {
    return {
      uid: evalueeId,
      portalId,
      registrationId: register.id || null,
      fullName: register.fullName,
      lowerCaseFullName: register.fullName.toLowerCase(),
      logonName: register.email || '',
      assessmentSettings: register.assessmentSettings,
      age: null,
      gender: null,
      completedMyProfile: null,
      status: 'new',
      occubrowseAccess: false,
      lastActivity: 'Invitation created',
      lastActivityDate: new Date(Date.now()),
      lastModifiedDate: new Date(Date.now()),
      createdDate: new Date(Date.now()),
      interestComplete: null,
      exercisesComplete: null,
      completedDate: null,
      counselor: register.counselor,
      interestStatus: '',
      exerciseStatus: '',
      counselorHistory: [],
      adjustedGrade: null,
      currentAssessmentId: assessmentId,
      identifiers: register.identifiers || [],
      identifierCategories: register.identifiers?.map(identifier => identifier.id) || [],
      consumerDirect: register.consumerDirect || false,
      userId: null,
      batchId: register.batchId || null,
      creatorId: register.creatorId || 'unknown',
      externalId: null,
      externalProvider: null,
    };
  }

  sendInvitation(evaluee: Evaluee, email: string): void {
    const invitationData = {
      to: email,
      template: {
        name: 'invitation-email',
        data: {
          name: evaluee.fullName,
          invitedBy: evaluee.counselor.name,
          portalUrl: environment.evalueeInvitationURL,
          id: evaluee.registrationId,
        },
      },
    };

    addDoc(collection(this.firestore, 'invitations'), <Invitation>invitationData)
      .then(() => {
        this.notificationService.openDismissibleSnackbar('Email queued for delivery!');
      })
      .catch(error => {
        console.error('Error adding invitation: ', error);
        this.notificationService.openDismissibleSnackbar('Failed to queue email for delivery');
      });
  }

  async sendBatchInvitations(evaluees: Evaluee[]): Promise<void> {
    const promises = evaluees.map(evaluee => {
      const invitationData = {
        to: evaluee.logonName,
        template: {
          name: 'invitation-email',
          data: {
            name: evaluee.fullName,
            invitedBy: evaluee.counselor.name,
            portalUrl: environment.evalueeInvitationURL,
            id: evaluee.registrationId,
          }
        }
      };

      return addDoc(collection(this.firestore, 'invitations'), invitationData);
    });

    await Promise.allSettled(promises).then(results => {
      if (!results.every(result => result.status === 'fulfilled')) {
        console.error('Failed to send batch invitations');
        this.notificationService.openDismissibleSnackbar('Failed to send invitations');
      }
    });
  }
}

// Exported outside the class for reuse
export function createAssessmentObject(evalueeId: string, portalId: string, assessmentId: string, counselorId?: string): Assessment {
  return {
    id: assessmentId,
    evalueeId,
    portalId,
    age: null,
    gender: null,
    exercisesComplete: null,
    interestsComplete: null,
    evalueeAnswers: [],
    taskResults: [],
    interestResults: [],
    aptitudeScores: [],
    interestScores: [],
    topInterestScores: [],
    currentGrade: null,
    completedDate: null,
    lastModifiedDate: new Date(Date.now()),
    interestInventoryType: 'career-scope',
    counselorId: counselorId || null,
    status: 'in progress',
  };
}